import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import {
  ScoringWeights,
  DEFAULT_SCORING_WEIGHTS,
  normalizeWeights,
  validateWeights,
  calculateWeightedScore
} from '../config/scoringConfig';
import { Lead } from '../types/Lead';

interface ScoringContextType {
  weights: ScoringWeights;
  updateWeights: (newWeights: ScoringWeights) => void;
  resetToDefaults: () => void;
  calculateCustomScore: (lead: Lead) => number;
  isCustomScoring: boolean;
}

const ScoringContext = createContext<ScoringContextType | undefined>(undefined);

const STORAGE_KEY = 'lead-scoring-weights';

interface ScoringProviderProps {
  children: ReactNode;
}

export const ScoringProvider: React.FC<ScoringProviderProps> = ({ children }) => {
  const [weights, setWeights] = useState<ScoringWeights>(DEFAULT_SCORING_WEIGHTS);

  // Load weights from localStorage on mount
  useEffect(() => {
    const savedWeights = localStorage.getItem(STORAGE_KEY);
    if (savedWeights) {
      try {
        const parsed = JSON.parse(savedWeights);
        if (validateWeights(parsed)) {
          setWeights(normalizeWeights(parsed));
        }
      } catch (error) {
        console.warn('Failed to parse saved scoring weights, using defaults');
      }
    }
  }, []);

  // Save weights to localStorage when they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(weights));
  }, [weights]);

  const updateWeights = (newWeights: ScoringWeights) => {
    if (validateWeights(newWeights)) {
      const normalized = normalizeWeights(newWeights);
      setWeights(normalized);
    }
  };

  const resetToDefaults = () => {
    setWeights({ ...DEFAULT_SCORING_WEIGHTS });
  };

  const calculateCustomScore = useCallback((lead: Lead): number => {
    try {
      // Safety check for required lead properties
      if (!lead || !lead.metrics || !lead.overallScore || !lead.processed) {
        console.warn('Lead missing required properties for scoring:', lead);
        return 0;
      }

      const score = calculateWeightedScore(lead.metrics, lead.overallScore.score, weights);

      // Validate the result
      if (typeof score !== 'number' || isNaN(score) || !isFinite(score)) {
        console.error('Invalid score calculated for lead:', lead.metadata?.companyName, {
          score,
          metrics: lead.metrics,
          overallScore: lead.overallScore.score,
          weights
        });
        // Fallback to the original overall score
        return lead.overallScore.score || 0;
      }

      return score;
    } catch (error) {
      console.error('Error calculating custom score for lead:', lead.metadata?.companyName, error);
      // Fallback to the original overall score
      return lead.overallScore?.score || 0;
    }
  }, [weights]);

  // Check if current weights differ from defaults (memoized for performance)
  const isCustomScoring = useMemo(() => {
    return Object.entries(weights).some(([key, value]) =>
      Math.abs(value - DEFAULT_SCORING_WEIGHTS[key as keyof ScoringWeights]) > 0.001
    );
  }, [weights]);

  const contextValue: ScoringContextType = {
    weights,
    updateWeights,
    resetToDefaults,
    calculateCustomScore,
    isCustomScoring,
  };

  return (
    <ScoringContext.Provider value={contextValue}>
      {children}
    </ScoringContext.Provider>
  );
};

export const useScoring = (): ScoringContextType => {
  const context = useContext(ScoringContext);
  if (!context) {
    throw new Error('useScoring must be used within a ScoringProvider');
  }
  return context;
};